import { type ApiProblem, type ApiSchemas, call } from '@rental/contracts/client'
import type { RentalChargeKind } from '@rental/domain'
import { format, formatDateTime, formatEuro, localized } from '@rental/i18n'
import { Button, Field, Notice, Textarea } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import { ChevronLeft, Plus, X } from 'lucide-react'
import { type FormEvent, useId, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useAccess } from '../access-context'
import { api } from '../api'
import { canDo } from '../booking-can'
import { bookingQuery } from '../booking-query'
import { UnsentInspectionNotice } from '../capture/CaptureUi'
import { useUnsentInspection } from '../capture/hooks'
import { ActionRail, RAIL_FORM, RAIL_STEPS } from '../components/ActionRail'
import { StatusBadge } from '../components/BookingItem'
import { MoneyStrip } from '../components/MoneyStrip'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { Section } from '../components/Section'
import { LoadState } from '../components/states'
import { FIELD, FIELD_ROW, StaffInput, StaffSelect } from '../components/ui'
import { excessKmPreview } from '../excess-km'
import { noImplicitSubmit } from '../forms'
import { readingKm } from '../handover/readiness'
import { returnReadiness } from '../handover/return-readiness'
import { useLocale } from '../i18n'
import { InspectionPanel, type MarkedDamage } from '../inspection/InspectionPanel'
import { parseEuroCents } from '../money'
import { problemOf } from '../problems'
import { refreshAfterChange } from '../refresh'
import { FUEL_LEVELS, fuelLabel } from '../rental'

const route = getRouteApi('/bookings/$code/return')

type Detail = ApiSchemas['StaffBookingDetail']
/** Fuel is the server's to charge, and a fine's fee the register's (S6); the counter types the rest. */
type ChargeKind = Exclude<RentalChargeKind, 'fuel' | 'fine_fee' | 'excess_km'>
/**
 * What a row typed by hand can be. A damage is charged from the diagram,
 * where it was marked at this return (J4): the server refuses one in words only.
 */
const TYPED_KINDS: readonly ChargeKind[] = ['late', 'cleaning', 'other']
/** The most charges one return takes (the API's own limit). */
const MAX_CHARGES = 20

interface ChargeRow {
  /** Rows come and go; this keeps each one's inputs its own. */
  key: number
  kind: ChargeKind
  description: string
  amount: string
  /** J4: the damage marked at this return that the row charges; its kind stays damage. */
  damageId?: string
}

/**
 * OPS-2 and OPS-3: take the vehicle back, with what the counter finds.
 * UI-1d: the steps on panes, and beside them, from 1024 px, the rail with the
 * one button and what it waits for, as at the hand-over.
 */
export function ReturnScreen() {
  const { locale, messages: m } = useLocale()
  const { code } = route.useParams()
  const upper = code.toUpperCase()
  const detail = useQuery(bookingQuery(code))
  const problem = problemOf(detail.error)
  const booking = detail.data?.booking
  return (
    <>
      <PageHeader
        back={
          <Link
            to="/bookings/$code"
            params={{ code: upper }}
            className="flex min-h-touch items-center gap-1 self-start"
          >
            <ChevronLeft aria-hidden="true" className="size-4" />
            {m.checkout.back}
          </Link>
        }
        title={format(m.giveBack.title, { code: upper })}
        status={booking ? <StatusBadge status={booking.status} /> : undefined}
        meta={
          booking
            ? [
                localized(booking.category.name, locale),
                // The car coming back, to check against the one at the counter.
                ...(booking.vehicle ? [booking.vehicle.plate] : []),
                `${formatDateTime(locale, booking.startsAt)} → ${formatDateTime(locale, booking.endsAt)}`,
              ].join(' · ')
            : undefined
        }
      />
      <LoadState data={detail.data} problem={problem}>
        {(loaded) => <ReturnForm detail={loaded} />}
      </LoadState>
    </>
  )
}

function ReturnForm({ detail }: { detail: Detail }) {
  const { locale, messages: m } = useLocale()
  const { takeOver } = useAccess()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { booking, rental } = detail
  const [km, setKm] = useState('')
  const [fuel, setFuel] = useState('')
  const [notes, setNotes] = useState('')
  const [charges, setCharges] = useState<ChargeRow[]>([])
  const [waiveExcessKm, setWaiveExcessKm] = useState(false)
  // A counter, not state: two rows added before a render still get their own keys.
  const keys = useRef(0)
  const newKey = () => {
    keys.current += 1
    return keys.current
  }
  // Each charge's heading names its fields: two charges' "Importo" are told apart.
  const chargeIds = useId()
  const chargeList = useRef<HTMLUListElement>(null)
  const addArea = useRef<HTMLDivElement>(null)
  // A charge added or removed takes its button's place in the focus: the new charge's first
  // field; after a removal, the charge now in its place, the one before, or Aggiungi.
  const focusCharge = (index: number) => {
    const rows = chargeList.current?.querySelectorAll<HTMLElement>(':scope > li')
    const row = rows?.[index] ?? rows?.[index - 1]
    const target =
      row?.querySelector<HTMLElement>('select, input') ?? addArea.current?.querySelector('button')
    target?.focus()
  }
  const [busy, setBusy] = useState(false)
  // Taken back: the screen stays as it is until the booking's page replaces it.
  const [leaving, setLeaving] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  // S4: the return is recorded once its inspection has reached the server from this iPad.
  const vehicleId = booking.vehicle?.id ?? null
  const inspection = useUnsentInspection(
    vehicleId ? { bookingCode: booking.code, stage: 'return', vehicleId } : null,
  )
  // S9: the office's, never a trip's: photos still unsent need the Owner at the counter.
  const may = canDo(detail.can)
  const canLeaveUnsent = may('override')
  const [leaveUnsent, setLeaveUnsent] = useState(false)
  const [unsentReason, setUnsentReason] = useState('')
  const leavingUnsent = inspection.unsent.length > 0 && leaveUnsent && canLeaveUnsent

  const state = returnReadiness({
    kmOut: rental?.kmOut ?? 0,
    km,
    fuel,
    inspection: {
      known: inspection.known,
      unsent: inspection.unsent.length,
      leaving: leavingUnsent,
      leaveReason: unsentReason,
    },
    charges,
  })
  const kmIn = readingKm(km)
  const amounts = charges.map((charge) => parseEuroCents(charge.amount))

  const update = (key: number, change: Partial<ChargeRow>) =>
    setCharges(charges.map((charge) => (charge.key === key ? { ...charge, ...change } : charge)))

  /** OPS-3, J4: a damage marked now joins the charges, its amount typed by the counter (Q-28). */
  const chargeDamage = (damage: MarkedDamage) => {
    const key = newKey()
    setCharges((rows) =>
      rows.some((row) => row.damageId === damage.id) || rows.length >= MAX_CHARGES
        ? rows
        : [
            ...rows,
            {
              key,
              kind: 'damage',
              description: format(m.inspection.chargeDescription, {
                zone: m.inspection.zones[damage.zone],
                description: damage.description,
              }).slice(0, 200),
              amount: '',
              damageId: damage.id,
            },
          ],
    )
  }
  const chargedDamages = new Set(charges.flatMap((row) => (row.damageId ? [row.damageId] : [])))

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!state.ready || kmIn === null) return
    setProblem(null)
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/bookings/{code}/return', {
        params: { path: { code: booking.code } },
        body: {
          kmIn,
          fuelIn: Number(fuel),
          waiveExcessKm,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
          charges: charges.map((charge, index) => ({
            kind: charge.kind,
            description: charge.description.trim(),
            amountCents: amounts[index] ?? 0,
            ...(charge.damageId ? { damageId: charge.damageId } : {}),
          })),
          ...(leavingUnsent
            ? {
                unsentInspection: {
                  count: inspection.unsent.length,
                  reason: unsentReason.trim(),
                },
              }
            : {}),
        },
      }),
    )
    if (result.ok) {
      // Busy until the booking's page is up: a second press must not take it back again.
      setLeaving(true)
      await refreshAfterChange(queryClient, booking.code)
      void navigate({
        to: '/bookings/$code',
        params: { code: booking.code },
        search: { done: 'return' },
      })
      return
    }
    if (takeOver(result.problem)) {
      setBusy(false)
      return
    }
    setProblem(result.problem)
    // Refused: the booking is read again, so a return already recorded on another iPad
    // says so. The button waits for that answer: a second press would be refused again.
    if (result.problem.kind === 'api') {
      await queryClient.invalidateQueries({ queryKey: ['booking', booking.code] })
    }
    setBusy(false)
  }

  // Only a vehicle out comes back (the server refuses any other): said, not offered. A
  // refused press that finds the vehicle back has lost its button with the form: what it
  // found takes the focus. Otherwise a polite status, heard if it comes while here.
  if (!rental || (booking.status !== 'checked_out' && !leaving)) {
    const tone = rental?.returnedAt ? 'info' : 'warning'
    const words = (
      <p>
        {rental?.returnedAt
          ? format(m.giveBack.alreadyBack, { date: formatDateTime(locale, rental.returnedAt) })
          : m.giveBack.notOut}
      </p>
    )
    return problem ? (
      <OutcomeNotice tone={tone}>{words}</OutcomeNotice>
    ) : (
      <Notice tone={tone} live="polite">
        {words}
      </Notice>
    )
  }

  const kmTooLow = kmIn !== null && kmIn < rental.kmOut
  const fuelLower = fuel !== '' && Number(fuel) < rental.fuelOut
  const kmPreview = excessKmPreview(rental, kmIn, detail.booking.billableDays)

  return (
    <form onSubmit={submit} onKeyDown={noImplicitSubmit} className={RAIL_FORM} noValidate>
      <div className={RAIL_STEPS}>
        <Section title={m.giveBack.readings}>
          <div className={FIELD_ROW}>
            <Field
              className={FIELD}
              label={m.giveBack.km}
              required
              hint={format(m.giveBack.kmHint, { km: rental.kmOut })}
              error={kmTooLow ? m.errors.codes.km_below_checkout : null}
            >
              {(control) => (
                <StaffInput
                  {...control}
                  inputMode="numeric"
                  autoComplete="off"
                  // Whole kilometres, seven digits at most, as the server takes them.
                  maxLength={7}
                  value={km}
                  onChange={(event) => setKm(event.target.value.replace(/\D/g, ''))}
                />
              )}
            </Field>
            <Field
              className={FIELD}
              label={m.giveBack.fuel}
              required
              hint={format(m.giveBack.fuelHint, { level: fuelLabel(rental.fuelOut, m) })}
            >
              {(control) => (
                <StaffSelect
                  {...control}
                  value={fuel}
                  onChange={(event) => setFuel(event.target.value)}
                >
                  <option value="" />
                  {FUEL_LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {fuelLabel(level, m)}
                    </option>
                  ))}
                </StaffSelect>
              )}
            </Field>
          </div>
          {fuelLower ? (
            <Notice tone="info" live="polite">
              <p>{m.giveBack.fuelCharge}</p>
            </Notice>
          ) : null}
          {kmPreview ? (
            <Notice tone="info" live="polite">
              <p>{format(m.giveBack.excessKmDriven, { km: kmPreview.driven })}</p>
              <p>{format(m.giveBack.excessKmIncluded, { km: kmPreview.included })}</p>
              <p>{format(m.giveBack.excessKmOver, { km: kmPreview.excess })}</p>
              <p>
                {m.giveBack.excessKmCharge}{' '}
                <span className={waiveExcessKm ? 'line-through' : undefined}>
                  {formatEuro(kmPreview.chargeCents, locale)}
                </span>
                {waiveExcessKm ? ` ${formatEuro(0, locale)}` : null}
              </p>
              {kmPreview.chargeCents > 0 ? (
                <label className="flex min-h-touch items-center gap-2">
                  <input
                    type="checkbox"
                    checked={waiveExcessKm}
                    onChange={(event) => setWaiveExcessKm(event.target.checked)}
                  />
                  {m.giveBack.waiveExcessKm}
                </label>
              ) : null}
            </Notice>
          ) : null}
          <Field
            label={m.giveBack.notes}
            hint={m.giveBack.notesHint}
            optionalLabel={m.newBooking.optional}
          >
            {(control) => (
              <Textarea
                {...control}
                className="rounded-sm"
                rows={3}
                maxLength={2000}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            )}
          </Field>
        </Section>

        <Section title={m.inspection.title}>
          <InspectionPanel
            code={booking.code}
            can={may}
            onCharge={chargeDamage}
            charged={chargedDamages}
            chargesFull={charges.length >= MAX_CHARGES}
          />
          <UnsentInspectionNotice
            stage="return"
            unsent={inspection.unsent}
            elsewhere={[]}
            known={inspection.known}
            canOverride={canLeaveUnsent}
            leave={leaveUnsent}
            onLeave={setLeaveUnsent}
            reason={unsentReason}
            onReason={setUnsentReason}
          />
        </Section>

        <Section title={m.giveBack.charges}>
          {charges.length === 0 ? (
            <p className="text-text-muted">{m.giveBack.noCharges}</p>
          ) : (
            <ul ref={chargeList} className="flex flex-col gap-3">
              {charges.map((charge, index) => (
                <li key={charge.key} className="flex flex-col gap-3 rounded-sm bg-surface-well p-4">
                  <div className="flex items-center justify-between gap-3">
                    <h3 id={`${chargeIds}-${charge.key}`} className="text-lg">
                      {format(m.giveBack.chargeNumber, { number: index + 1 })}
                    </h3>
                    <Button
                      variant="outline"
                      className="rounded-sm"
                      aria-label={format(m.giveBack.removeCharge, { number: index + 1 })}
                      onClick={() => {
                        flushSync(() =>
                          setCharges(charges.filter((other) => other.key !== charge.key)),
                        )
                        focusCharge(index)
                      }}
                    >
                      <X aria-hidden="true" className="size-4" />
                    </Button>
                  </div>
                  {/* The two short fields side by side where they fit; the words on a row of their own. */}
                  <fieldset
                    aria-labelledby={`${chargeIds}-${charge.key}`}
                    className={`${FIELD_ROW} min-w-0`}
                  >
                    <Field className={FIELD} label={m.giveBack.chargeKind}>
                      {(control) => (
                        <StaffSelect
                          {...control}
                          value={charge.kind}
                          onChange={(event) =>
                            update(charge.key, { kind: event.target.value as ChargeKind })
                          }
                        >
                          {(charge.damageId ? (['damage'] as const) : TYPED_KINDS).map((kind) => (
                            <option key={kind} value={kind}>
                              {m.chargeKinds[kind]}
                            </option>
                          ))}
                        </StaffSelect>
                      )}
                    </Field>
                    <Field
                      className={FIELD}
                      label={m.giveBack.chargeAmount}
                      required
                      error={
                        charge.amount.trim() !== '' && amounts[index] === null
                          ? m.payment.amountInvalid
                          : null
                      }
                    >
                      {(control) => (
                        <StaffInput
                          {...control}
                          inputMode="decimal"
                          autoComplete="off"
                          value={charge.amount}
                          onChange={(event) => update(charge.key, { amount: event.target.value })}
                        />
                      )}
                    </Field>
                    <Field
                      className="min-w-0 basis-full"
                      label={m.giveBack.chargeDescription}
                      required
                    >
                      {(control) => (
                        <StaffInput
                          {...control}
                          maxLength={200}
                          value={charge.description}
                          onChange={(event) =>
                            update(charge.key, { description: event.target.value })
                          }
                        />
                      )}
                    </Field>
                  </fieldset>
                </li>
              ))}
            </ul>
          )}
          {charges.length < MAX_CHARGES ? (
            <div ref={addArea}>
              <Button
                variant="outline"
                className="rounded-sm"
                onClick={() => {
                  // The last one allowed takes the button away: the focus goes to its fields.
                  flushSync(() =>
                    setCharges([
                      ...charges,
                      { key: newKey(), kind: 'other', description: '', amount: '' },
                    ]),
                  )
                  focusCharge(charges.length)
                }}
              >
                <Plus aria-hidden="true" className="size-4" />
                {m.giveBack.addCharge}
              </Button>
            </div>
          ) : (
            <p className="text-sm text-text-muted">
              {format(m.giveBack.chargesFull, { max: MAX_CHARGES })}
            </p>
          )}
        </Section>
      </div>

      <ActionRail
        title={m.giveBack.railTitle}
        action={m.giveBack.submit}
        detailsLabel={m.giveBack.railDetails}
        ready={state.ready}
        busy={busy || leaving}
        reasons={state.reasons.map((reason) => m.giveBack.why[reason])}
        problem={problem}
      >
        {/* What is owed is information: the server takes the vehicle back whatever it says. */}
        <div className="flex flex-col gap-2">
          <MoneyStrip money={detail.money} />
          <p className="text-sm text-text-muted">{m.giveBack.moneyHint}</p>
        </div>
      </ActionRail>
    </form>
  )
}
